import { Box, Button, HStack, Icon, Menu, Portal, Table, Text } from "@chakra-ui/react";
import { HiEllipsisHorizontal, HiMiniArrowUp, HiMiniArrowDown } from "react-icons/hi2";
import dayjs from "dayjs";
import ResponsibleUserSelect from "@/components/ui/scenarios/ResponsibleUserSelect";

const headerStyle = {
    color: "text.grey",
    fontWeight: 600,
    fontSize: "xs",
    textTransform: "uppercase",
    letterSpacing: "wider",
    py: 3,
    px: 4,
    border: "none",
    whiteSpace: "nowrap",
};

const cellStyle = { py: 3, px: 4, border: "none", verticalAlign: "middle" };

const TYPE_LABELS = { SalesManager: "Sales Manager", Recruiter: "Recruiter" };

const personName = (p) =>
    p ? (`${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || p.email || "—") : "—";

const fmt = (d) => (d ? dayjs(d).format("DD.MM.YYYY HH:mm") : "—");

const SortableHeader = ({ label, field, sortBy, sortDirection, onSort }) => {
    const active = sortBy === field;
    return (
        <Table.ColumnHeader
            {...headerStyle}
            cursor="pointer"
            userSelect="none"
            onClick={() => onSort(field)}
            aria-sort={active ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}
            _hover={{ color: "white" }}
        >
            <HStack gap={1}>
                <Text as="span">{label}</Text>
                {active && (
                    <Icon fontSize={14}>
                        {sortDirection === "asc" ? <HiMiniArrowUp /> : <HiMiniArrowDown />}
                    </Icon>
                )}
            </HStack>
        </Table.ColumnHeader>
    );
};

const RowActions = ({ scenario, onOpen, onDuplicate, onDelete }) => (
    <Menu.Root positioning={{ placement: "bottom-end" }}>
        <Menu.Trigger asChild>
            <Button size="xs" variant="outlined" aria-label="Scenario actions" px={2} onClick={(e) => e.stopPropagation()}>
                <Icon fontSize={18}><HiEllipsisHorizontal /></Icon>
            </Button>
        </Menu.Trigger>
        <Portal>
            <Menu.Positioner>
                <Menu.Content bg="bg.tab" borderColor="border.primary" borderWidth={1}>
                    <Menu.Item value="open" onClick={() => onOpen(scenario)} color="gray.200" _hover={{ bg: "bg.dark" }}>
                        Open
                    </Menu.Item>
                    <Menu.Item value="duplicate" onClick={() => onDuplicate(scenario)} color="gray.200" _hover={{ bg: "bg.dark" }}>
                        Duplicate
                    </Menu.Item>
                    <Menu.Item value="delete" onClick={() => onDelete(scenario)} color="red.300" _hover={{ bg: "bg.dark" }}>
                        Delete
                    </Menu.Item>
                </Menu.Content>
            </Menu.Positioner>
        </Portal>
    </Menu.Root>
);

const ScenariosTable = ({
    scenarios,
    query,
    startIndex = 0,
    onSort,
    onOpen,
    onDuplicate,
    onDelete,
    responsibleUsers,
    responsibleUsersLoading,
    onResponsibleChange,
    pendingResponsibleId,
    dimmed,
}) => (
    <Box w="full" bg="bg.card" borderWidth={1} borderColor="border.secondary" borderRadius={12} overflow="hidden">
        <Box overflowX="auto" opacity={dimmed ? 0.6 : 1} transition="opacity 0.15s">
            <Table.Root size="sm" minW="980px">
                <Table.Header>
                    <Table.Row bg="bg.card">
                        <Table.ColumnHeader {...headerStyle} textAlign="right" w="1%">#</Table.ColumnHeader>
                        <SortableHeader label="Name" field="name" sortBy={query.sortBy} sortDirection={query.sortDirection} onSort={onSort} />
                        <Table.ColumnHeader {...headerStyle}>Type</Table.ColumnHeader>
                        <Table.ColumnHeader {...headerStyle}>Client</Table.ColumnHeader>
                        <Table.ColumnHeader {...headerStyle}>Responsible</Table.ColumnHeader>
                        <SortableHeader label="Created" field="createdAt" sortBy={query.sortBy} sortDirection={query.sortDirection} onSort={onSort} />
                        <SortableHeader label="Updated" field="updatedAt" sortBy={query.sortBy} sortDirection={query.sortDirection} onSort={onSort} />
                        <Table.ColumnHeader {...headerStyle} textAlign="right">Actions</Table.ColumnHeader>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {scenarios.map((s, i) => (
                        <Table.Row
                            key={s.id}
                            bg="bg.card"
                            _hover={{ bg: "bg.highlight" }}
                            transition="background 0.15s"
                            cursor="pointer"
                            onClick={() => onOpen(s)}
                        >
                            <Table.Cell {...cellStyle} textAlign="right" color="text.grey" fontSize="sm">
                                {startIndex + i + 1}
                            </Table.Cell>
                            <Table.Cell {...cellStyle} maxW="320px">
                                <Text color="white" fontSize="sm" fontWeight={500} truncate title={s.name}>{s.name}</Text>
                            </Table.Cell>
                            <Table.Cell {...cellStyle}>
                                <Text color="text.grey" fontSize="sm">{s.type ? (TYPE_LABELS[s.type] ?? s.type) : "—"}</Text>
                            </Table.Cell>
                            <Table.Cell {...cellStyle} maxW="200px">
                                <Text color="text.grey" fontSize="sm" truncate>{personName(s.owner)}</Text>
                            </Table.Cell>
                            <Table.Cell {...cellStyle} onClick={(e) => e.stopPropagation()}>
                                <ResponsibleUserSelect
                                    value={s.responsibleUser?.id ?? null}
                                    users={responsibleUsers}
                                    loading={responsibleUsersLoading || pendingResponsibleId === s.id}
                                    onChange={(userId) => onResponsibleChange(s, userId)}
                                />
                            </Table.Cell>
                            <Table.Cell {...cellStyle}>
                                <Text color="text.grey" fontSize="sm" whiteSpace="nowrap">{fmt(s.createdAtUtc)}</Text>
                            </Table.Cell>
                            <Table.Cell {...cellStyle}>
                                <Text color="text.grey" fontSize="sm" whiteSpace="nowrap">{fmt(s.editedAtUtc)}</Text>
                            </Table.Cell>
                            <Table.Cell {...cellStyle} textAlign="right" onClick={(e) => e.stopPropagation()}>
                                <RowActions scenario={s} onOpen={onOpen} onDuplicate={onDuplicate} onDelete={onDelete} />
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table.Root>
        </Box>
    </Box>
);

export default ScenariosTable;
